import type { GraphQLEmployee } from 'modules/org_chart/lib/employeeUtils'
import type { PositionNodeData } from 'modules/org_chart/models/NodeData'

export interface ManagedPositionLike {
  id: string
  managerId?: string | null
}

export const buildDirectReportsByManagerId = (allEmployees: GraphQLEmployee[]) => {
  const directReportsByManagerId = new Map<string, GraphQLEmployee[]>()

  allEmployees.forEach((employee) => {
    if (!employee.managerId) {
      return
    }

    const managedEmployees = directReportsByManagerId.get(employee.managerId)

    if (managedEmployees) {
      managedEmployees.push(employee)
    } else {
      directReportsByManagerId.set(employee.managerId, [employee])
    }
  })

  return directReportsByManagerId
}

export const buildDescendantCountByEmployeeId = (
  allEmployees: GraphQLEmployee[],
  directReportsByManagerId: Map<string, GraphQLEmployee[]>
) => {
  const descendantCountByEmployeeId = new Map<string, number>()
  const visitState = new Map<string, 'visiting' | 'done'>()

  const countDescendants = (employeeId: string): number => {
    const state = visitState.get(employeeId)

    if (state === 'visiting') {
      return 0
    }

    if (state === 'done') {
      return descendantCountByEmployeeId.get(employeeId) ?? 0
    }

    visitState.set(employeeId, 'visiting')

    const directReports = directReportsByManagerId.get(employeeId) ?? []
    let descendantCount = directReports.length

    directReports.forEach((directReport) => {
      descendantCount += countDescendants(directReport.id)
    })

    visitState.set(employeeId, 'done')
    descendantCountByEmployeeId.set(employeeId, descendantCount)

    return descendantCount
  }

  allEmployees.forEach((employee) => {
    countDescendants(employee.id)
  })

  return descendantCountByEmployeeId
}

export const buildPositionNodesByManagerId = (positions: ManagedPositionLike[]) => {
  const positionNodesByManagerId = new Map<string, PositionNodeData[]>()

  positions.forEach((position) => {
    if (!position.managerId) {
      return
    }

    const existingPositionNodes = positionNodesByManagerId.get(position.managerId)
    const positionNode = { type: 'position', positionId: position.id } satisfies PositionNodeData

    if (existingPositionNodes) {
      existingPositionNodes.push(positionNode)
    } else {
      positionNodesByManagerId.set(position.managerId, [positionNode])
    }
  })

  return positionNodesByManagerId
}
